import { type ConnectedProps, connect } from 'react-redux';
import { type AnyAction } from 'redux';
import {
  StyledForceDismissButton,
  StyledToastLayout,
  ToastContent,
  StyledToastAction,
  StyledToastSecondaryMessage,
} from './styles';
import { GenericFocusTrap } from '@/components/uielements/genericFocusTrap';
import basketActions from '@/redux/basket/actions';
import globalActions from '@/redux/global/actions';
import { type RootState } from '@/types/app';
import { CONNECTION_RETRY_LABEL } from '@/utils/errorReporter';

let timeout: ReturnType<typeof setTimeout>;
const timedDismiss = (display: boolean, dismissToast: () => void, forceDismiss?: boolean) => {
  if (forceDismiss) {
    return;
  }
  if (display) {
    timeout = setTimeout(dismissToast, 20000);
  }
};

const Toast = ({ toast, dismissToast, reopenBasketItemForOptions, dispatchRetry }: ReduxProps) => {
  if (!toast.display) {
    return null;
  }

  // Narrowed once here so the JSX below reads `reopen.x` rather than asserting inside its own guard.
  const { reopen, retry } = toast;

  return (
    <GenericFocusTrap
      focusTrapOptions={{
        clickOutsideDeactivates: true,
        initialFocus: '#Global-Toast',
      }}
    >
      <StyledToastLayout
        data-css-override="StyledToastLayout"
        $status={toast.status}
        id="Global-Toast"
      >
        {timedDismiss(toast.display, dismissToast, toast.forceDismiss)}
        {/*
          aria-live sits on the container rather than the first line so both lines are
          announced — a second line outside the region would be silent (TEAM-14387).
        */}
        <ToastContent aria-live="polite">
          <p
            onFocus={() => clearTimeout(timeout)}
            dangerouslySetInnerHTML={{ __html: toast.message }}
          />
          {/*
            Rendered as plain text rather than through the innerHTML sink above, so a
            two-line toast needs no markup in its message (TEAM-14387).
          */}
          {toast.secondaryMessage ? (
            <StyledToastSecondaryMessage>{toast.secondaryMessage}</StyledToastSecondaryMessage>
          ) : null}
          {toast.refresh ? (
            <StyledToastAction
              onClick={() => window.location.reload()}
              aria-label="Click to refresh the page"
            >
              Refresh Page
            </StyledToastAction>
          ) : null}
          {/*
            TEAM-15138: reopens the named cart line's customizer so the diner can satisfy a required
            option group the menu no longer accepts. Independent of `refresh` above — a reload
            reopens nothing, and the two affordances answer different failures.

            The label is rendered as plain text, not through the innerHTML sink above, because it
            interpolates an operator-authored option group name.

            No `aria-label`, unlike the refresh button above. That one earns its distinct label
            because "Refresh Page" is vague about what gets refreshed; "Choose your Rice" already
            names the thing being chosen, so an `aria-label` repeating it verbatim adds nothing and
            is one more place the wording can drift out of step with what is on screen. The visible
            text is the accessible name — `toast.test.tsx` asserts the button by role and name so
            that stays true.
          */}
          {reopen ? (
            <StyledToastAction
              onClick={() =>
                reopenBasketItemForOptions(reopen.basketItemIndex, {
                  expectedSignature: reopen.expectedSignature,
                })
              }
            >
              {reopen.ctaLabel}
            </StyledToastAction>
          ) : null}
          {/*
            TEAM-14780: re-dispatches the request that failed at the network layer, so a
            connection toast offers the same "Try again" as the account pages and auth forms.
            Dismisses first: if the retry fails again it raises a fresh toast.
          */}
          {retry ? (
            <StyledToastAction
              onClick={() => {
                dismissToast();
                dispatchRetry(retry);
              }}
            >
              {CONNECTION_RETRY_LABEL}
            </StyledToastAction>
          ) : null}
        </ToastContent>

        <StyledForceDismissButton onClick={dismissToast} aria-label="Click to close alert">
          &times;
        </StyledForceDismissButton>
      </StyledToastLayout>
    </GenericFocusTrap>
  );
};

const mapStateToProps = (state: RootState) => ({
  toast: state.app.global.toast,
});

const mapDispatchToProps = {
  dismissToast: globalActions.dismissToast,
  reopenBasketItemForOptions: basketActions.reopenBasketItemForOptions,
  dispatchRetry: (action: AnyAction) => action,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(Toast);
